import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Flag, Plus, Filter, CheckCircle2, AlertTriangle, XCircle,
  Clock, User, ChevronDown, Undo2, Trash2, Star
} from 'lucide-react';
import { toast } from 'sonner';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  High: { badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border-red-200 dark:border-red-800/40', dot: 'bg-red-500' },
  Medium: { badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-amber-200 dark:border-amber-800/40', dot: 'bg-amber-500' },
  Low: { badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-blue-200 dark:border-blue-800/40', dot: 'bg-blue-500' },
};

const TYPE_ICONS = {
  'Injury Risk': { icon: AlertTriangle, color: 'text-red-500' },
  'Low Performance': { icon: ChevronDown, color: 'text-amber-500' },
  'Attendance Issue': { icon: Clock, color: 'text-orange-500' },
  'Behavioral Concern': { icon: XCircle, color: 'text-purple-500' },
  'Academic Risk': { icon: AlertTriangle, color: 'text-yellow-600 dark:text-yellow-400' },
  'High Potential': { icon: Star, color: 'text-emerald-500' },
};

const AthleteFlags = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [flags, setFlags] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filters, setFilters] = useState({ status: 'Active', severity: '', flag_type: '', squad: '' });
  const [newFlag, setNewFlag] = useState({ athlete_id: '', flag_type: '', severity: '', description: '' });
  const [summary, setSummary] = useState({ total_active: 0, by_severity: {}, by_type: {} });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.severity && filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.flag_type && filters.flag_type !== 'all') params.append('flag_type', filters.flag_type);
      if (filters.squad && filters.squad !== 'all') params.append('squad', filters.squad);

      const [flagsRes, summaryRes, athletesRes, squadsRes] = await Promise.all([
        fetch(`${API}/api/flags?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/flags/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/athletes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/squads`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (flagsRes.ok) setFlags(await flagsRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (athletesRes.ok) setAthletes(await athletesRes.json());
      if (squadsRes.ok) {
        const data = await squadsRes.json();
        setSquads(data.squads || []);
      }
    } catch (err) {
      console.error('Fetch flags error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newFlag.athlete_id || !newFlag.flag_type || !newFlag.severity || !newFlag.description) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await fetch(`${API}/api/flags`, { method: 'POST', headers, body: JSON.stringify(newFlag) });
      if (res.ok) {
        toast.success('Flag created successfully');
        setShowCreateDialog(false);
        setNewFlag({ athlete_id: '', flag_type: '', severity: '', description: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create flag');
      }
    } catch { toast.error('Failed to create flag'); }
  };

  const handleResolve = async (flagId) => {
    try {
      const res = await fetch(`${API}/api/flags/${flagId}`, { method: 'PUT', headers, body: JSON.stringify({ status: 'Resolved' }) });
      if (res.ok) { toast.success('Flag resolved'); fetchData(); }
    } catch { toast.error('Failed to resolve flag'); }
  };

  const handleUnresolve = async (flagId) => {
    try {
      const res = await fetch(`${API}/api/flags/${flagId}`, { method: 'PUT', headers, body: JSON.stringify({ status: 'Active' }) });
      if (res.ok) { toast.success('Flag reactivated'); fetchData(); }
    } catch { toast.error('Failed to reactivate flag'); }
  };

  const handleDelete = async (flagId) => {
    if (!window.confirm('Delete this flag permanently?')) return;
    try {
      const res = await fetch(`${API}/api/flags/${flagId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Flag deleted'); fetchData(); }
      else { toast.error('Only admins can delete flags'); }
    } catch { toast.error('Failed to delete flag'); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isAdmin = user?.role === 'Admin';
  const canManage = user?.role === 'Admin' || user?.role === 'Staff';

  const FLAG_TYPES = ['Injury Risk', 'Low Performance', 'Attendance Issue', 'Behavioral Concern', 'Academic Risk', 'High Potential'];
  const SEVERITIES = ['High', 'Medium', 'Low'];

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="athlete-flags-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading uppercase text-pfap-green dark:text-gray-100" data-testid="flags-heading">
              Alerts & Flags
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-body">
              Track athlete risks, issues, and important flags
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-pfap-green hover:bg-pfap-green/90 text-white h-9 px-4"
              data-testid="add-flag-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Flag
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="flags-summary">
          <SummaryCard label="Active Flags" value={summary.total_active} color="red" />
          <SummaryCard label="High Severity" value={summary.by_severity?.High || 0} color="red" />
          <SummaryCard label="Medium Severity" value={summary.by_severity?.Medium || 0} color="amber" />
          <SummaryCard label="Low Severity" value={summary.by_severity?.Low || 0} color="blue" />
        </div>

        {/* Filters */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-4" data-testid="flags-filters">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={14} className="text-gray-400" />
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                <SelectTrigger className="w-[130px] h-8 text-xs bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.severity} onValueChange={(v) => setFilters(f => ({ ...f, severity: v }))}>
                <SelectTrigger className="w-[130px] h-8 text-xs bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="filter-severity">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  {SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.flag_type} onValueChange={(v) => setFilters(f => ({ ...f, flag_type: v }))}>
                <SelectTrigger className="w-[160px] h-8 text-xs bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {FLAG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.squad} onValueChange={(v) => setFilters(f => ({ ...f, squad: v }))}>
                <SelectTrigger className="w-[130px] h-8 text-xs bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="filter-squad">
                  <SelectValue placeholder="Squad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Squads</SelectItem>
                  {squads.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {(filters.severity || filters.flag_type || filters.squad) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-gray-500 dark:text-gray-400"
                  onClick={() => setFilters({ status: filters.status, severity: '', flag_type: '', squad: '' })}
                  data-testid="clear-filters-btn"
                >
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Flags List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          </div>
        ) : flags.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
            <CardContent className="py-12 text-center">
              <Flag size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">No flags found</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4">Adjust filters above, or raise a new flag to track a concern.</p>
              {user?.role !== 'Player' && user?.role !== 'Parent' && (
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-pfap-green hover:bg-pfap-green/90"
                  data-testid="create-flag-empty-cta"
                >
                  <Plus size={16} className="mr-1.5" /> Raise a Flag
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="flags-list">
            {flags.map((flag) => {
              const typeInfo = TYPE_ICONS[flag.flag_type] || { icon: Flag, color: 'text-gray-500' };
              const TypeIcon = typeInfo.icon;
              const sevStyle = SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.Low;
              const isResolved = flag.status === 'Resolved';

              return (
                <Card
                  key={flag.id}
                  className={`bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 transition-all ${
                    isResolved ? 'opacity-70' : ''
                  }`}
                  data-testid={`flag-${flag.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isResolved ? 'bg-gray-100 dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700/50'
                      }`}>
                        {isResolved ? (
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        ) : (
                          <TypeIcon size={18} className={typeInfo.color} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" data-testid={`flag-athlete-${flag.id}`}>
                            {flag.athlete_name}
                          </span>
                          <Badge className={`text-[10px] px-1.5 py-0 border ${sevStyle.badge}`} data-testid={`flag-severity-${flag.id}`}>
                            {flag.severity}
                          </Badge>
                          <Badge className="text-[10px] px-1.5 py-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600" data-testid={`flag-type-${flag.id}`}>
                            {flag.flag_type}
                          </Badge>
                          {isResolved && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40">
                              Resolved
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1.5 line-clamp-2">{flag.description}</p>
                        <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500">
                          <span className="flex items-center gap-1"><User size={10} /> {flag.created_by_name}</span>
                          <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(flag.created_at)}</span>
                          {flag.athlete_squad && <span>{flag.athlete_squad}</span>}
                          {isResolved && flag.resolved_at && (
                            <span className="text-emerald-500 dark:text-emerald-400">Resolved {formatDate(flag.resolved_at)} by {flag.resolved_by}</span>
                          )}
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isResolved ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                              onClick={() => handleResolve(flag.id)}
                              data-testid={`resolve-flag-${flag.id}`}
                            >
                              <CheckCircle2 size={14} className="mr-1" /> Resolve
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                              onClick={() => handleUnresolve(flag.id)}
                              data-testid={`unresolve-flag-${flag.id}`}
                            >
                              <Undo2 size={14} className="mr-1" /> Reactivate
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => handleDelete(flag.id)}
                              data-testid={`delete-flag-${flag.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Flag Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="create-flag-dialog">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-gray-100 font-heading uppercase text-lg">Add Flag</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Athlete</label>
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={newFlag.athlete_id}
                  onChange={(v) => setNewFlag(f => ({ ...f, athlete_id: v }))}
                  placeholder="Search & select athlete..."
                  data-testid="create-flag-athlete"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Flag Type</label>
                <Select value={newFlag.flag_type} onValueChange={(v) => setNewFlag(f => ({ ...f, flag_type: v }))}>
                  <SelectTrigger className="w-full h-9 text-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="create-flag-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLAG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Severity</label>
                <Select value={newFlag.severity} onValueChange={(v) => setNewFlag(f => ({ ...f, severity: v }))}>
                  <SelectTrigger className="w-full h-9 text-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="create-flag-severity">
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Description</label>
                <textarea
                  value={newFlag.description}
                  onChange={(e) => setNewFlag(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the issue or flag..."
                  className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-pfap-green/30 resize-none"
                  data-testid="create-flag-description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="create-flag-submit">
                Create Flag
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const SummaryCard = ({ label, value, color }) => {
  const colors = {
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  };
  return (
    <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
      <CardContent className="p-3">
        <p className={`text-2xl font-bold ${colors[color]?.split(' ').slice(2).join(' ') || 'text-gray-900 dark:text-gray-100'}`}>
          {value}
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
};

export default AthleteFlags;
